import { Link, useNavigate } from 'react-router-dom'
import { Trash2, ShoppingBag, ArrowRight, FileText, AlertTriangle } from 'lucide-react'
import { useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useCartStore } from '../store/cartStore'
import { QuantityControl, EmptyState } from '../components/ui'
import { templateService, authService } from '../services/api'
import { useAuth } from '../context/AuthContext'

function formatPrice(p) {
  if (!p && p !== 0) return 'R 0.00'
  return `R ${parseFloat(p).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`
}

function getCategoryEmoji(product) {
  const combined = `${product?.description || product?.product_name || product?.name || ''} ${product?.brand || ''} ${product?.category_name || ''}`.toLowerCase()
  if (/whiskey|whisky|bourbon|scotch/.test(combined)) return '🥃'
  if (/vodka/.test(combined))   return '🍸'
  if (/gin/.test(combined))     return '🌿'
  if (/tequila/.test(combined)) return '🌵'
  if (/rum/.test(combined))     return '🏝️'
  if (/brandy/.test(combined))  return '🍂'
  if (/liqueur/.test(combined)) return '✨'
  if (/champagne|sparkling/.test(combined)) return '🥂'
  if (/cider/.test(combined))   return '🍋'
  if (/rtd cocktail/.test(combined)) return '🍹'
  if (/rtd/.test(combined))     return '🥤'
  if (/beer|lager|ale/.test(combined)) return '🍺'
  if (/wine/.test(combined))    return '🍷'
  if (/energy/.test(combined))  return '⚡'
  if (/juice/.test(combined))   return '🍊'
  if (/water/.test(combined))   return '🫧'
  if (/cigarette|tobacco/.test(combined)) return '🚬'
  if (/gift/.test(combined))    return '🎁'
  return '🍾'
}

export default function Cart() {
  const { items, updateQuantity, removeItem, clearCart, getTotal, count } = useCartStore()
  const [clearConfirm, setClearConfirm] = useState(false)
  const { isAuthenticated } = useAuth()

  const { data: customerProfile } = useQuery({
    queryKey: ['customer', 'me'],
    queryFn: authService.customerMe,
    enabled: isAuthenticated,
    staleTime: 60 * 1000,
    retry: false,
  })

  const canOrder = !customerProfile || customerProfile.can_order !== false

  const subtotal = getTotal()
  const total = subtotal   // no delivery charge — B2B distributor

  if (!items.length) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12 animate-fade-in">
        <EmptyState
          icon="🛒"
          title="Your cart is empty"
          description="Browse our products and add something you like."
          action={<Link to="/shop" className="btn-primary inline-flex items-center gap-2"><ShoppingBag size={16} />Start Shopping</Link>}
        />
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 animate-fade-in">
      <h1 className="section-title mb-6">
        Shopping Cart <span className="text-gray-400 text-xl font-body font-normal">({count} items)</span>
      </h1>

      <div className="grid lg:grid-cols-3 gap-8">
        {/* Items */}
        <div className="lg:col-span-2 space-y-3">
          {items.map(item => (
            <CartItem key={item.id} item={item} onUpdate={updateQuantity} onRemove={removeItem} />
          ))}

          <div className="flex justify-between pt-2">
            <button
              onClick={() => setClearConfirm(true)}
              className="text-sm text-red-500 hover:text-red-700 flex items-center gap-1.5 font-display"
            >
              <Trash2 size={14} />
              Clear Cart
            </button>
            <Link to="/shop" className="text-sm text-primary hover:underline font-display flex items-center gap-1">
              Continue Shopping <ArrowRight size={14} />
            </Link>
          </div>
        </div>

        {/* Summary */}
        <div className="space-y-4">
          {/* Summary */}
          <div className="card p-5 space-y-3">
            <h3 className="font-display font-semibold text-gray-800 dark:text-white mb-2">Order Summary</h3>

            <div className="space-y-2 text-sm">
              <div className="flex justify-between text-gray-600 dark:text-gray-300">
                <span>Subtotal ({count} items)</span>
                <span>{formatPrice(subtotal)}</span>
              </div>
            </div>

            <div className="border-t border-gray-100 dark:border-gray-700 pt-3">
              <div className="flex justify-between font-display font-bold text-gray-900 dark:text-white text-lg">
                <span>Total</span>
                <span className="text-primary dark:text-red-400">{formatPrice(total)}</span>
              </div>
              <p className="text-xs text-gray-400 mt-1">Including VAT</p>
            </div>

            <SaveAsTemplateButton />

            {!canOrder && (
              <div className="flex items-start gap-2 p-3 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-700 dark:text-amber-300 text-xs mb-2">
                <AlertTriangle size={14} className="shrink-0 mt-0.5"/>
                <span>Your account is on hold due to an outstanding balance. Please contact us to place orders.</span>
              </div>
            )}

            {canOrder ? (
              <Link to="/checkout" className="btn-primary w-full flex items-center justify-center gap-2 mt-2">
                Proceed to Checkout <ArrowRight size={16} />
              </Link>
            ) : (
              <button disabled className="w-full mt-2 py-3 rounded-xl bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 font-display font-semibold text-sm cursor-not-allowed flex items-center justify-center gap-2">
                <AlertTriangle size={15}/> Ordering Suspended
              </button>
            )}

            <button
              onClick={() => setClearConfirm(true)}
              className="w-full mt-3 flex items-center justify-center gap-2 py-2.5 rounded-xl border border-red-200 dark:border-red-900/50 text-red-500 hover:bg-red-500 hover:text-white hover:border-red-500 transition-colors text-sm font-display font-semibold"
            >
              <Trash2 size={14} /> Clear Cart
            </button>
          </div>
        </div>
      </div>

      {/* ── Clear Cart Confirmation Modal ── */}
      {clearConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setClearConfirm(false)}
          />
          <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-6 max-w-sm w-full animate-fade-in">
            {/* Icon */}
            <div className="w-14 h-14 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <Trash2 size={26} className="text-red-500" />
            </div>

            <h3 className="font-display font-bold text-center text-gray-900 dark:text-white text-lg mb-1">
              Clear your cart?
            </h3>
            <p className="text-sm text-center text-gray-500 dark:text-gray-400 mb-1">
              This will remove all <span className="font-semibold text-gray-700 dark:text-gray-300">{count} item{count !== 1 ? 's' : ''}</span> from your cart.
            </p>
            <p className="text-xs text-center text-gray-400 dark:text-gray-500 mb-6">
              This action cannot be undone.
            </p>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  clearCart()
                  setClearConfirm(false)
                }}
                className="flex-1 bg-red-500 hover:bg-red-600 active:bg-red-700 text-white rounded-xl py-3 text-sm font-display font-semibold transition-colors flex items-center justify-center gap-2"
              >
                <Trash2 size={15} /> Yes, Clear It
              </button>
              <button
                onClick={() => setClearConfirm(false)}
                className="flex-1 btn-outline py-3 text-sm rounded-xl"
              >
                Keep Shopping
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function CartItem({ item, onUpdate, onRemove }) {
  const navigate = useNavigate()
  const name = item.product?.product_name || item.product?.description || item.product?.name || item.name || 'Product'
  const price = item.price || item.product?.inclprice2 || item.product?.inclprice || 0
  const imageUrl = item.product?.primary_image ? `/MappedData/${item.product.primary_image}` : null
  const productId = item.product?.id || item.product_id || item.id
  const [imgError, setImgError] = useState(false)

  return (
    <div className="card p-4 flex gap-4 group hover:border-primary/20 transition-colors border border-transparent">
      {/* Clickable image */}
      <button
        onClick={() => productId && navigate(`/product/${productId}`)}
        className="w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden bg-gray-50 dark:bg-gray-700 relative flex-shrink-0 hover:ring-2 hover:ring-primary/40 transition-all"
        title={`View ${name}`}
      >
        {imageUrl && !imgError ? (
          <img src={imageUrl} alt={name} onError={() => setImgError(true)} className="w-full h-full object-contain p-1" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-3xl">{getCategoryEmoji(item.product)}</div>
        )}
      </button>

      <div className="flex-1 min-w-0">
        <div className="flex justify-between gap-4">
          <div className="min-w-0 flex-1">
            {/* Clickable product name */}
            <button
              onClick={() => productId && navigate(`/product/${productId}`)}
              className="text-left w-full group/name"
            >
              <h4 className="font-display font-semibold text-gray-900 dark:text-white text-sm leading-snug line-clamp-2 group-hover/name:text-primary transition-colors">
                {name}
              </h4>
            </button>
            {item.product?.brand && (
              <p className="text-xs text-gray-400 mt-0.5">{item.product.brand}</p>
            )}
            {/* Stock code in the "empty middle" space */}
            {item.product?.stock_code && (
              <p className="text-xs text-gray-300 dark:text-gray-600 mt-1 font-mono">{item.product.stock_code}</p>
            )}
          </div>
          <button
            onClick={() => onRemove(item.id)}
            className="text-gray-300 hover:text-red-500 transition-colors flex-shrink-0"
          >
            <Trash2 size={16} />
          </button>
        </div>

        <div className="flex items-center justify-between mt-3">
          <QuantityControl
            quantity={item.quantity}
            onChange={(q) => onUpdate(item.id, q)}
          />
          <div className="text-right">
            <div className="font-display font-bold text-primary dark:text-red-400">
              R {(price * item.quantity).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
            </div>
            <div className="text-xs text-gray-400">
              R {parseFloat(price).toFixed(2)} each
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function SaveAsTemplateButton() {
  const { items } = useCartStore()
  const { isAuthenticated } = useAuth()
  const [open, setOpen] = useState(false)
  const [name, setName] = useState('')

  const mut = useMutation({
    mutationFn: () => templateService.create({
      name: name.trim(),
      items: items.map(i => ({
        stock_code: i.product?.stock_code || '',
        quantity: i.quantity,
        note: null,
      })).filter(i => i.stock_code),
    }),
    onSuccess: () => { setOpen(false); setName(''); import('react-hot-toast').then(m => m.default.success('Saved as template')) },
    onError:   () => import('react-hot-toast').then(m => m.default.error('Failed to save template')),
  })

  if (!isAuthenticated) return null

  return (
    <div className="mt-1">
      {!open ? (
        <button
          onClick={() => setOpen(true)}
          className="w-full flex items-center justify-center gap-2 py-2.5 text-sm text-primary dark:text-primary-light border border-primary/30 rounded-xl hover:bg-primary/5 transition-colors font-display font-medium"
        >
          <FileText size={14} /> Save Cart as Template
        </button>
      ) : (
        <div className="border border-primary/30 rounded-xl p-3 space-y-2">
          <input
            className="input-field text-sm py-2"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="Template name…"
            autoFocus
          />
          <div className="flex gap-2">
            <button onClick={() => mut.mutate()} disabled={!name.trim() || mut.isLoading} className="btn-primary flex-1 py-2 text-sm">
              {mut.isLoading ? 'Saving…' : 'Save'}
            </button>
            <button onClick={() => { setOpen(false); setName('') }} className="btn-outline py-2 text-sm px-3">Cancel</button>
          </div>
        </div>
      )}
    </div>
  )
}

